import React from 'react';
import { Check } from 'lucide-react';
import { StatusReason, StatusReasonMetric, TrainingStatus } from '../../services/trainingProgressService';
import StatusChip from './StatusChip';

/**
 * feat-195 — the Status explanation card. ONE content component, TWO presentations
 * (design brief §1.6): a floating hover card on the roster's Status pill (D4b/D5) and a
 * persistent panel inside the crew dialog (D4c). Only the header band and the outer
 * wrapper differ; every string, every number and every row is shared — which is also
 * what makes the dialog the ACCESSIBLE FALLBACK, since nothing there is hover-gated.
 *
 * ══ THE RULE THAT GOVERNS THIS FILE (spec D6) ══════════════════════════════════════
 * Every number and every sentence comes from the backend. This component performs
 * **ZERO threshold arithmetic** — no comparison of a value against a threshold, no
 * grace-line subtraction, no "which axis triggered" derivation. AC4 is a grep of the
 * diff. A TypeScript re-derivation would silently drift from the real verdict, which is
 * the exact failure this feature exists to prevent. Everything below is a pure mapping
 * from a payload field to a class name or a glyph.
 *
 * Three specific traps that follow from it:
 *  1. `value === null → "—"` is the ONLY dash rule. Never draw an em-dash for a `0` you
 *     were handed — that is a display decision, and display decisions the frontend makes
 *     are what this contract exists to prevent. `value` is a REQUIRED property with a
 *     nullable type; if you find yourself testing `value === undefined`, the contract
 *     has been violated upstream — report it, do not defend against it.
 *  2. `sentences` ARRAY ORDER **IS** RENDER ORDER. Join with line breaks only. Never
 *     sort, reverse, select, or interpolate a number into one — they arrive complete,
 *     with their numbers already inside.
 *  3. ITERATE `metrics[]`; never branch per metric. The ordered list exists precisely so
 *     the renderer is correct even for a metric it has never heard of.
 */

export type StatusReasonPresentation = 'floating' | 'inline';

export interface StatusReasonCardProps {
  reason: StatusReason;
  /**
   * The row's own `status` — NOT `reason.status`. The echo inside the payload exists to
   * be CHECKED (QA asserts they are equal), not to be rendered; keying the header off
   * `row.status` is what makes a divergence visible instead of self-consistent.
   */
  status: TrainingStatus;
  presentation?: StatusReasonPresentation;
}

/**
 * One breakdown row. Every branch below reads a payload field; none computes one.
 *
 * | payload                      | render                                              |
 * |------------------------------|-----------------------------------------------------|
 * | `value === null`             | "—" (the O1 exhausted-lessons row)                  |
 * | `counted === false`          | whole row dimmed, value loses its weight            |
 * | `meets === false`            | coral on BOTH the value and the threshold           |
 * | `meets === true`             | a success check glyph after the threshold cell       |
 * | `threshold !== null`         | "needs {threshold}"                                 |
 * | `threshold === null && note` | the note, VERBATIM — never synthesised in TSX       |
 * | neither                      | empty cell (the `worked_days` denominator row)      |
 *
 * `threshold === null` has TWO causes — the carve-out silenced the axis, and no minimum
 * exists yet (grace line 0). They are deliberately INDISTINGUISHABLE here: the renderer
 * needs exactly one rule and needs it for both. Anyone who must tell them apart reads
 * `counted`; the human-facing difference is carried by the backend-owned `note`.
 */
function MetricRow({ metric, triggered }: { metric: StatusReasonMetric; triggered: boolean }) {
  const dimmed = metric.counted === false;

  // `tabular-nums` is required, not cosmetic: without it a 1-digit and a 2-digit value
  // break the column edge.
  const valueTone = dimmed
    ? 'text-inkMuted'
    : metric.meets === false
      ? 'font-bold text-coral'
      : triggered
        ? 'font-bold text-success'
        : 'font-bold text-ink';

  const thresholdTone = metric.meets === false
    ? 'text-coral'
    : dimmed
      ? 'text-inkMuted/80'
      : 'text-inkMuted';

  // "needs N" when there is a line; otherwise the backend's qualifier; otherwise
  // nothing. Note that the check glyph is bound to `meets`, NOT to `threshold` — at
  // worked_days == 1 the grace line is 0, so the axis is counted and trivially cleared
  // and must KEEP its check even though no numeric line renders (ADR §B6a / design §2.9).
  const showsThreshold = metric.threshold !== null;

  return (
    <div
      className={`grid items-baseline gap-2 ${dimmed ? 'opacity-55' : ''}`}
      style={{ gridTemplateColumns: '1fr auto auto' }}
      data-testid={`status-reason-metric-${metric.key}`}
    >
      <span className="text-xs text-inkMuted">{metric.label}</span>
      <span className={`text-sm tabular-nums text-right ${metric.value === null ? 'text-inkMuted/60' : valueTone}`}>
        {metric.value === null ? '—' : metric.value}
      </span>
      <span
        className={`w-[62px] text-right inline-flex items-center justify-end gap-1 leading-tight ${
          showsThreshold ? 'text-xs' : 'text-[10px]'
        } ${thresholdTone}`}
      >
        {showsThreshold ? `needs ${metric.threshold}` : metric.note ?? ''}
        {metric.meets === true && (
          <Check size={11} strokeWidth={3.5} className="text-success shrink-0" aria-hidden="true" />
        )}
      </span>
    </div>
  );
}

export function StatusReasonCard({ reason, status, presentation = 'floating' }: StatusReasonCardProps) {
  const floating = presentation === 'floating';
  const triggered = new Set(reason.triggered_by);

  // The curriculum-complete clause is `sentences[0]` and is set in bold — under O1 it is
  // the only thing distinguishing an exhausted card from a no-worked-days one, so it has
  // to carry more weight than before (design §2.5). The ONE exception is the double-cause
  // card (exhausted AND no recent shifts): there `no_recent_shifts` leads because it is
  // the actual verdict, and the exhaustion fact follows as a bare statement — bolding the
  // first sentence there would emphasise the wrong clause (§2c).
  //
  // Keyed on `counted`, NOT on `lessons_exhausted && status !== 'no_recent_shifts'`.
  // Those are equivalent — the carve-out fires iff `exhausted AND worked_days > 0`, and
  // `worked_days > 0` is exactly `status !== 'no_recent_shifts'` — but the second form
  // RE-ENCODES the backend's own prepend condition in TSX, which is two encodings of one
  // rule and the drift D6 exists to prevent, one level down in presentation. This form
  // reads a semantic fact the payload states outright: an axis was silenced, so the card
  // leads by saying why. (Found by QA, 2026-07-29.)
  //
  // RESIDUAL, stated rather than papered over: "when the carve-out fired, the first
  // sentence is the curriculum clause" is still an inference about backend copy order.
  // Only a backend-owned emphasis hint removes it — per-sentence `{text, emphasis}`
  // instead of `sentences: string[]`, which is an ADR §B1 change and a FOLLOWUP.
  const leadsWithCurriculum = reason.metrics.some((m) => !m.counted);

  return (
    <div
      data-testid="status-reason-card"
      className={
        floating
          ? 'w-[320px] rounded-xl overflow-hidden text-left'
          : 'rounded-xl border border-softBorder bg-cream/60 overflow-hidden text-left'
      }
    >
      {/* Floating only: the dialog already renders the chip directly above this panel,
          so a second one there would be a duplicate, not a header. */}
      {floating && (
        <div className="bg-cream-dark px-4 py-2.5">
          <StatusChip kind={status} />
        </div>
      )}

      {/* The window line, verbatim. `end_exclusive` is one PAST the last in-window day;
          subtracting a day to render "Jul 29" would be date arithmetic in the frontend,
          which is why the backend ships the whole header line as `display` (ADR §B3). */}
      <div className="px-4 pt-3 pb-1 text-[11px] text-inkMuted">{reason.window.display}</div>

      <div className="px-4 pb-3 pt-1 space-y-1.5">
        {reason.metrics.map((m) => (
          <MetricRow key={m.key} metric={m} triggered={triggered.has(m.key)} />
        ))}
      </div>

      {reason.sentences.length > 0 && (
        <div className="border-t border-softBorder px-4 py-3">
          {reason.sentences.map((sentence, i) => {
            // The grace parenthetical is the one sentence design sets apart, and it is
            // identified by its own leading "(" (design's card anatomy). Everything else
            // renders identically — the frontend does not decide which sentence matters.
            const parenthetical = sentence.startsWith('(');
            return (
              <p
                key={i}
                className={`leading-snug ${i > 0 ? 'mt-1' : ''} ${
                  parenthetical
                    ? 'text-xs text-inkMuted'
                    : `text-sm text-ink${i === 0 && leadsWithCurriculum ? ' font-bold' : ''}`
                }`}
              >
                {sentence}
              </p>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default StatusReasonCard;
